<template>
    <div
        :class="{
            'has-text-grey-light is-not-allowed': locked && !canViewAdministratively,
        }"
        class="section-template-wrapper tw:flex tw:w-full tw:min-w-0 tw:overflow-hidden"
        @click="toggleMenu"
        @keyup="toggleMenu"
    >
        <div
            class="section-item__text tw:w-full tw:flex tw:justify-between tw:items-start tw:min-w-0 tw:gap-1 tw:overflow-hidden"
            :class="{ isCurrentWeek, 'is-not-allowed': locked }"
            :title="titleText"
        >
            <p
                v-tooltip.right.hover="navTitleTooltip(item.text)"
                class="nav-title-clamp-2 tw:min-w-0 tw:flex-1"
            >
                {{ item.text }}
            </p>

            <e-icon
                size="small"
                :icon="menuClass"
                class="section-item__chevron tw:flex-shrink-0"
                :class="{ 'is-invisible': isBreak }"
            />
        </div>

        <div class="section-item__icons">
            <locked-item
                v-if="hasPrerequisite && locked"
                :item="{
                    type: 'prerequisite',
                    namespace: item.type,
                    position: 'is-right',
                    prereqTitle,
                }"
            />

            <locked-item
                v-if="notVisible"
                :item="{
                    type: 'visibleAt',
                    namespace: item.type,
                    visibleAt: titleText,
                    position: 'is-right',
                }"
            />

            <locked-item
                v-if="isBreak"
                :item="{
                    type: 'break',
                    namespace: 'Week Break',
                    position: 'is-right',
                }"
            />

            <e-icon v-if="item.completed" icon="check" />
        </div>
    </div>
</template>

<script>
import LockedItem from '@/components/nested-list/LockedItem.vue';
import SidebarMixin from '@/mixins/Sidebar.mixin.js';
import moment from '@/plugins/vue-moment.js';
import { navTitleTooltip } from '@/utilities/longTitleTooltip.js';

export default {
    name: 'SectionTemplate',

    components: { LockedItem },

    mixins: [SidebarMixin],

    props: {
        autoExpand: {
            type: String,
            required: false,
            default: '',
        },
    },

    emits: ['toggling-menu'],

    data: () => ({
        showingMenu: false,
    }),

    computed: {
        menuClass() {
            return this.showingMenu ? 'pi pi-chevron-down' : 'pi pi-chevron-right';
        },

        namespace() {
            return this.template ? 'SectionTemplates' : 'Sections';
        },

        section() {
            let week = null;

            if (this.isBreak) week = this.item.week_number;

            if (this.isWeek) week = this.item.display_week_number;

            return {
                type: this.item.section_type,
                week,
            };
        },

        isBreak() {
            return this.item.section_type === 'break';
        },

        isWeek() {
            return this.item.section_type === 'week';
        },

        isBreakOrWeek() {
            return this.isBreak || this.isWeek;
        },

        dateRange() {
            if (this.isBreakOrWeek && !this.$route.name.includes('temp')) {
                const carriesOverToNextYear = this.checkIfDateRangeCarriesOverToNextYear(
                    this.item.week_start,
                    this.item.week_end
                );
                const weekStart = this.accountForDST(this.item.week_start, 'start', carriesOverToNextYear);
                const weekEnd = this.accountForDST(this.item.week_end, 'end', carriesOverToNextYear);

                return `${weekStart}-${weekEnd}`;
            }

            return null;
        },

        /**
         * @returns {boolean}
         */
        isCurrentWeek() {
            return (
                this.item.type === 'section' &&
                this.item.section_type === 'week' &&
                moment().isBetween(this.item.week_start, this.item.week_end)
            );
        },
    },

    watch: {
        autoExpand(autoExpand) {
            this.search.do(this.item.items, 'items', item => {
                if (item.uuid === autoExpand) {
                    this.toggleMenu();
                }
            });
        },
    },

    mounted() {
        if (this.isCurrentWeek) this.toggleMenu();
    },

    methods: {
        navTitleTooltip,

        toggleMenu() {
            if (this.locked && !this.canViewAdministratively) return null;

            this.showingMenu = !this.showingMenu;

            return this.$emit('toggling-menu', {
                display: this.showingMenu,
                item: this.item,
            });
        },

        accountForDST(date, weekParam, carriesOverToNextYear) {
            const method = weekParam === 'start' ? 'add' : 'subtract';

            const momentDate = moment.utc(date).tz(this.$timezone.name);

            momentDate[method](1, 'hours');

            if (carriesOverToNextYear) {
                return momentDate.format('MM/DD/YY');
            }

            return momentDate.format('MM/DD');
        },

        checkIfDateRangeCarriesOverToNextYear(weekStart, weekEnd) {
            return moment(weekEnd).isAfter(weekStart, 'year');
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.section-item {
    font-size: 1em !important;

    &__icons,
    &__text {
        p {
            padding-left: 0;
            text-transform: none;
        }

        & div > span {
            font-size: $size-6;
            color: $text;
        }
    }

    &__icons {
        color: $text;
        position: relative;
        top: -1px;
    }

    &__text {
        &:hover {
            color: $primary;

            &.isLocked {
                color: $text-subtle;
            }
        }

        &.isCurrentWeek {
            color: $primary;
            position: relative;

            &::after {
                background: $primary;
                border-radius: 3px;
                content: '';
                height: 6px;
                position: absolute;
                right: #{calc($column-gap / -1)};
                top: 50%;
                transform: translateY(-50%);
                width: 6px;
            }
        }

        .icon {
            color: $primary;
        }
    }

    & + .nested-list.menu-list {
        padding-left: 1.5rem;
    }
}

.menu-label {
    padding: $menu-list-link-padding;
}

// Style chevron inline with the title row
.section-template-wrapper {
    position: relative;
}

.section-item__chevron {
    font-size: 1rem;
    color: var(--p-text-muted-color, $text-subtle);
}
</style>
